import React, { useMemo, useState, createContext, useContext } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { IconButton, Box, Toolbar, useMediaQuery, Typography, Button } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Slide from '@mui/material/Slide';
import { TransitionProps } from '@mui/material/transitions';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Students from './pages/Students';
import Cameras from './pages/Cameras';
import Attendance from './pages/Attendance';
import Users from './pages/Users';
import Profile from './pages/Profile';
import Reports from './pages/Reports';
import SuperAdminDashboard from './pages/SuperAdminDashboard';
import AdminDashboard from './pages/AdminDashboard';
import LiveView from './pages/LiveView';
import SchoolManagement from './pages/SchoolManagement';
import ClassSessions from './pages/ClassSessions';
import Courses from './pages/Courses';
import Grades from './pages/Grades';
import Exams from './pages/Exams';
import EnrollmentManagement from './pages/EnrollmentManagement';
import AdminSettings from './pages/AdminSettings';
import SubscriptionManagement from './pages/SubscriptionManagement';
import SchoolSubscriptionPage from './pages/SchoolSubscription';
import TokenManagementPage from './pages/TokenManagementPage';
import InvoiceList from "./pages/InvoiceList";
import InvoiceDetail from "./pages/InvoiceDetail";
import SystemSettings from "./pages/SystemSettings";
import ClassLevelManagement from "./pages/ClassLevelManagement";
import BulkGradeEntry from './components/BulkGradeEntry';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './theme/ThemeProvider';
import SidebarNav from './components/SidebarNav';
import TopBar from './components/TopBar';
import { FeedbackProvider } from './context/FeedbackContext';
import ResetPassword from './pages/ResetPassword';
import SchoolApplication from './pages/SchoolApplication';
import SchoolApplications from './pages/SchoolApplications';
import LandingPage from './pages/LandingPage';
import Communications from './pages/Communications';
import SchoolCommunications from './pages/SchoolCommunications';
import GradeDetailsSender from './pages/GradeDetailsSender';
import ReportVerification from './pages/ReportVerification';
import ClassTeacherAssignment from './pages/ClassTeacherAssignment';
import SignatureManagement from './pages/SignatureManagement';
import MySignature from './pages/MySignature';
import Quizzes from './pages/Quizzes';
import QuizzesAttempts from './pages/QuizzesAttempts';
import QuizzesResultsReview from './pages/QuizzesResultsReview';
import StudentDashboard from './pages/StudentDashboard';
import MyAttendance from './pages/MyAttendance';
import MyGrades from './pages/MyGrades';
import SendNotificationPage from './pages/SendNotificationPage';
import MpesaTransactions from './pages/MpesaTransactions';
import SchoolPayments from './pages/SchoolPayments';

// Import token refresh test utilities for debugging
import './utils/tokenRefreshTest';



const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, user } = useAuth();
  console.log('ProtectedRoute:', { isAuthenticated, user });
  return (isAuthenticated && user) ? (
    <>{children}</>
  ) : (
    <Navigate to="/login" />
  );
};

// Route guard for login page - redirect authenticated users to their dashboard
const LoginRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, user } = useAuth();
  
  if (isAuthenticated && user) {
    // Redirect to appropriate dashboard based on role
    switch (user.role) {
      case 'superuser':
        return <Navigate to="/superadmin-dashboard" replace />;
      case 'admin':
        return <Navigate to="/admin-dashboard" replace />;
      case 'student':
        return <Navigate to="/student-dashboard" replace />;
      default:
        return <Navigate to="/dashboard" replace />;
    }
  }
  
  return <>{children}</>;
};

function RoleBasedDashboard() {
  const { user } = useAuth();
  if (!user) return null;
  if (user.role === 'superuser') return <SuperAdminDashboard />;
  if (user.role === 'admin') return <AdminDashboard />;
  if (user.role === 'student') return <StudentDashboard />;
  return <div>Unauthorized</div>;
}

// Main application layout with sidebar and topbar
const MainLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
  const [aboutOpen, setAboutOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <>
      <TopBar onToggleSidebar={() => setSidebarCollapsed((c) => !c)} />
      <Box sx={{ display: 'flex' }}>
        <SidebarNav collapsed={sidebarCollapsed} onToggleCollapse={() => setSidebarCollapsed((c) => !c)} />
        <Box 
          component="main" 
          sx={{ 
            flexGrow: 1, 
            p: 3, 
            transition: 'margin 0.2s'
          }}
        >
          <Toolbar />
          {children}
          {/* Floating About Widget */}
          <Box sx={{ position: 'fixed', bottom: 32, right: 32, zIndex: 2000 }}>
            <IconButton
              color="primary"
              size="large"
              sx={{
                background: 'linear-gradient(135deg, #1E3A8A 60%, #F97316 100%)',
                color: '#fff',
                boxShadow: '0 4px 16px rgba(30,58,138,0.15)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #F97316 0%, #1E3A8A 100%)',
                },
              }}
              onClick={() => setAboutOpen(true)}
            >
              <InfoOutlinedIcon fontSize="inherit" />
            </IconButton>
          </Box>
          <Dialog open={aboutOpen} onClose={() => setAboutOpen(false)} maxWidth="xs" fullWidth>
            <DialogTitle sx={{ fontWeight: 700, color: '#1E3A8A', textAlign: 'center' }}>About A3S Systems</DialogTitle>
            <DialogContent sx={{ textAlign: 'center', pb: 2 }}>
              <Box sx={{ mb: 2 }}>
                <img src="/static/logos/cvision_logo.svg" alt="CVision Logo" style={{ width: 64, height: 64, marginBottom: 8 }} />
              </Box>
              <Typography variant="body1" sx={{ mb: 2 }}>
                <b>A3S Systems</b> provides a modern, AI-powered attendance management platform for schools and organizations. It leverages facial recognition and smart analytics to automate and secure attendance tracking.
              </Typography>
              <Box sx={{ textAlign: 'left', mb: 1 }}>
                <Typography variant="subtitle2" sx={{ color: '#1E3A8A', fontWeight: 600, mb: 1 }}>Key Features:</Typography>
                <ul style={{ textAlign: 'left', margin: 0, paddingLeft: 18, fontSize: 15 }}>
                  <li><b>AI-Powered Attendance:</b> Automated, contactless check-in using facial recognition.</li>
                  <li><b>Manual Review:</b> Admins can review and confirm or correct recognition events.</li>
                  <li><b>Real-Time Analytics:</b> Instantly view attendance stats, trends, and reports.</li>
                  <li><b>Comprehensive Reporting:</b> Export attendance data as CSV or PDF for compliance and insights.</li>
                  <li><b>Role-Based Access:</b> Admin, teacher, and user dashboards for secure, tailored access.</li>
                  <li><b>Easy Enrollment:</b> Manage students, courses, and sessions with intuitive tools.</li>
                  <li><b>Mobile Friendly:</b> Responsive design for use on any device.</li>
                </ul>
              </Box>
              <Typography variant="caption" color="text.secondary">
                A3S Systems helps you save time, reduce errors, and improve engagement with smart, secure, and user-friendly attendance management.
              </Typography>
              <Box sx={{ mt: 3 }}>
                <Typography variant="caption" color="text.secondary">
                  Developed by{' '}
                  <a href="https://github.com/simlexx-k" target="_blank" rel="noopener noreferrer" style={{ color: '#1E3A8A', textDecoration: 'none', fontWeight: 600 }}>
                    Simon Kosgei
                  </a>
                </Typography>
              </Box>
            </DialogContent>
            <DialogActions sx={{ justifyContent: 'center', pb: 2 }}>
              <Button onClick={() => setAboutOpen(false)} color="primary" variant="contained" sx={{ minWidth: 100 }}>Close</Button>
            </DialogActions>
          </Dialog>
        </Box>
      </Box>
    </>
  );
};

function RoleProtectedRoute({ children, roles }: { children: React.ReactNode; roles: string[] }) {
  const { isAuthenticated, user } = useAuth();
  if (!isAuthenticated || !user || !roles.includes(user.role)) {
    return <Navigate to="/login" />;
  }
  return <>{children}</>;
}

function App() {
  return (
    <ThemeProvider>
      <FeedbackProvider>
        <AuthProvider>
            <Router>
              <Routes>
                {/* Login page - standalone without layout */}
                <Route path="/login" element={<LoginRoute>
                  <Login />
                </LoginRoute>} />
                <Route path="/reset-password" element={<ResetPassword />} />
                
                {/* All other routes - with main layout */}
                <Route path="/" element={<LandingPage />} />
                <Route path="/dashboard" element={
                  <ProtectedRoute>
                    <MainLayout>
                      <RoleBasedDashboard />
                    </MainLayout>
                  </ProtectedRoute>
                } />
                <Route path="/superadmin-dashboard" element={
                  <ProtectedRoute>
                    <MainLayout>
                      <SuperAdminDashboard />
                    </MainLayout>
                  </ProtectedRoute>
                } />
                <Route path="/admin-dashboard" element={
                  <ProtectedRoute>
                    <MainLayout>
                      <AdminDashboard />
                    </MainLayout>
                  </ProtectedRoute>
                } />
                <Route path="/students" element={
                  <ProtectedRoute>
                    <MainLayout>
                      <Students />
                    </MainLayout>
                  </ProtectedRoute>
                } />
                <Route path="/enrollment-management" element={
                  <ProtectedRoute>
                    <MainLayout>
                      <EnrollmentManagement />
                    </MainLayout>
                  </ProtectedRoute>
                } />
                <Route path="/cameras" element={
                  <ProtectedRoute>
                    <MainLayout>
                      <Cameras />
                    </MainLayout>
                  </ProtectedRoute>
                } />
                <Route path="/attendance" element={
                  <ProtectedRoute>
                    <MainLayout>
                      <Attendance />
                    </MainLayout>
                  </ProtectedRoute>
                } />
                <Route path="/users" element={
                  <ProtectedRoute>
                    <MainLayout>
                      <Users />
                    </MainLayout>
                  </ProtectedRoute>
                } />
                <Route path="/live/:cameraId" element={
                  <ProtectedRoute>
                    <MainLayout>
                      <LiveView />
                    </MainLayout>
                  </ProtectedRoute>
                } />
                <Route path="/profile" element={
                  <ProtectedRoute>
                    <MainLayout>
                      <Profile />
                    </MainLayout>
                  </ProtectedRoute>
                } />
                <Route path="/reports" element={
                  <ProtectedRoute>
                    <MainLayout>
                      <Reports />
                    </MainLayout>
                  </ProtectedRoute>
                } />
                <Route path="/school-management" element={
                  <ProtectedRoute>
                    <MainLayout>
                      <SchoolManagement />
                    </MainLayout>
                  </ProtectedRoute>
                } />
                <Route path="/class-sessions" element={
                  <ProtectedRoute>
                    <MainLayout>
                      <ClassSessions />
                    </MainLayout>
                  </ProtectedRoute>
                } />
                <Route path="/courses" element={
                  <ProtectedRoute>
                    <MainLayout>
                      <Courses />
                    </MainLayout>
                  </ProtectedRoute>
                } />
                <Route path="/grades" element={
                  <ProtectedRoute>
                    <MainLayout>
                      <Grades />
                    </MainLayout>
                  </ProtectedRoute>
                } />
                <Route path="/bulk-grading" element={
                  <ProtectedRoute>
                    <MainLayout>
                      <BulkGradeEntry />
                    </MainLayout>
                  </ProtectedRoute>
                } />
                <Route path="/exams" element={
                  <ProtectedRoute>
                    <MainLayout>
                      <Exams />
                    </MainLayout>
                  </ProtectedRoute>
                } />
                <Route path="/admin-settings" element={
                  <ProtectedRoute>
                    <MainLayout>
                      <AdminSettings />
                    </MainLayout>
                  </ProtectedRoute>
                } />
                <Route path="/subscription-management" element={
                  <ProtectedRoute>
                    <MainLayout>
                      <SubscriptionManagement />
                    </MainLayout>
                  </ProtectedRoute>
                } />
                <Route path="/school-subscription" element={
                  <ProtectedRoute>
                    <MainLayout>
                      <SchoolSubscriptionPage />
                    </MainLayout>
                  </ProtectedRoute>
                } />
                <Route path="/token-management" element={
                  <ProtectedRoute>
                    <MainLayout>
                      <TokenManagementPage />
                    </MainLayout>
                  </ProtectedRoute>
                } />
                <Route path="/invoices" element={
                  <ProtectedRoute>
                    <MainLayout>
                      <InvoiceList />
                    </MainLayout>
                  </ProtectedRoute>
                } />
                <Route path="/invoices/:id" element={
                  <ProtectedRoute>
                    <MainLayout>
                      <InvoiceDetail />
                    </MainLayout>
                  </ProtectedRoute>
                } />
                <Route path="/school-invoices" element={
                  <ProtectedRoute>
                    <MainLayout>
                      <InvoiceList />
                    </MainLayout>
                  </ProtectedRoute>
                } />
                <Route path="/system-settings" element={
                  <ProtectedRoute>
                    <MainLayout>
                      <SystemSettings />
                    </MainLayout>
                  </ProtectedRoute>
                } />
                <Route path="/class-level-management" element={
                  <ProtectedRoute>
                    <MainLayout>
                      <ClassLevelManagement />
                    </MainLayout>
                  </ProtectedRoute>
                } />
                <Route path="/apply" element={<SchoolApplication />} />
                <Route path="/school-applications" element={
                  <ProtectedRoute>
                    <MainLayout>
                      <SchoolApplications />
                    </MainLayout>
                  </ProtectedRoute>
                } />
                <Route path="/communications" element={
                  <ProtectedRoute>
                    <MainLayout>
                      <Communications />
                    </MainLayout>
                  </ProtectedRoute>
                } />
                <Route path="/school-communications" element={
                  <ProtectedRoute>
                    <MainLayout>
                      <SchoolCommunications />
                    </MainLayout>
                  </ProtectedRoute>
                } />
                <Route path="/grade-details-sender" element={
                  <ProtectedRoute>
                    <MainLayout>
                      <GradeDetailsSender />
                    </MainLayout>
                  </ProtectedRoute>
                } />
                <Route path="/verify-report/:reportToken" element={<ReportVerification />} />
                <Route path="/class-teacher-assignment" element={<ProtectedRoute><MainLayout><ClassTeacherAssignment /></MainLayout></ProtectedRoute>} />
                <Route path="/signature-management" element={
                  <ProtectedRoute>
                    <MainLayout>
                      <SignatureManagement />
                    </MainLayout>
                  </ProtectedRoute>
                } />
                <Route path="/my-signature" element={
                  <ProtectedRoute>
                    <MainLayout>
                      <MySignature />
                    </MainLayout>
                  </ProtectedRoute>
                } />
                <Route path="/quizzes" element={<ProtectedRoute><MainLayout><Quizzes /></MainLayout></ProtectedRoute>} />
                <Route path="/quizzes/attempts" element={<ProtectedRoute><MainLayout><QuizzesAttempts /></MainLayout></ProtectedRoute>} />
                <Route path="/quizzes/results-review" element={<ProtectedRoute><MainLayout><QuizzesResultsReview /></MainLayout></ProtectedRoute>} />
                <Route path="/student-dashboard" element={<ProtectedRoute><MainLayout><StudentDashboard /></MainLayout></ProtectedRoute>} />
                <Route path="/myattendance" element={<ProtectedRoute><MainLayout><MyAttendance /></MainLayout></ProtectedRoute>} />
                <Route path="/mygrades" element={<ProtectedRoute><MainLayout><MyGrades /></MainLayout></ProtectedRoute>} />
                <Route path="/send-notification" element={
                  <RoleProtectedRoute roles={["superuser", "admin"]}>
                    <MainLayout>
                      <SendNotificationPage />
                    </MainLayout>
                  </RoleProtectedRoute>
                } />
                <Route path="/mpesa-transactions" element={
                  <RoleProtectedRoute roles={["superuser"]}>
                    <MainLayout>
                      <MpesaTransactions />
                    </MainLayout>
                  </RoleProtectedRoute>
                } />
                <Route path="/school-payments" element={
                  <RoleProtectedRoute roles={["admin"]}>
                    <MainLayout>
                      <SchoolPayments />
                    </MainLayout>
                  </RoleProtectedRoute>
                } />
              </Routes>
            </Router>
          </AuthProvider>
        </FeedbackProvider>
      </ThemeProvider>
    );
  }

export default App;
